import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../analytics';
import { MAX_SPEED_MIN, MAX_SPEED_MAX } from '../lib/motionPace';
import prefersReducedMotion from '../lib/prefersReducedMotion';
import forceMuted from '../lib/forceMuted';
import {
  ASSETS,
  HERO_CELLS,
  CONST_CELLS,
  SAMPLE,
} from './motionPaceHeroData';
import MotionChartHero, { TimeRuler, FilmStrip } from './motionpace/MotionChartHero';
import './MotionPacePage.css';

// MotionPace feature landing page (#544), ported from the approved comp at
// tweedia-comps/19-motionpace-landing/.
//
// The route is gated at BUILD time, not runtime — see MOTIONPACE_ROUTE in
// scripts/prerender.routes.js. This component knows nothing about that; it just
// has to render the same page for a crawler with no JavaScript as for a signed-in
// human, which is why there is no data fetching and no feature-flag read here.
//
// TWO KINDS OF NUMBER LIVE ON THIS PAGE, AND THEY MUST NOT BE CONFUSED:
//
//  1. PRODUCT CAPABILITIES — what the Max speed slider can do for anyone. Those
//     come from MAX_SPEED_RANGE below.
//  2. ONE REAL RENDER — the Deck Build benchmark the hero chart visualises. Those
//     come from `SAMPLE` in motionPaceHeroData.js and are only ever written as
//     statements about that render ("this render's ceiling", "peaked at 5.6x").
//
// The comp mixed the two in one stat card ("Still stretches compress up to 6x",
// which was the sample's ceiling presented as the product's). That is corrected
// here; see MAX_SPEED_RANGE.

// The Max speed slider's range, READ FROM THE MODULE THAT ENFORCES IT.
// lib/motionPace.js clamps the slider to MAX_SPEED_MIN..MAX_SPEED_MAX, so
// deriving the copy from those constants is the honesty contract the door pages
// follow (#378/#384): a marketing number cannot outlive the limit it describes.
//
// This matters for merge ORDER, not just for drift. #551 widens the ceiling from
// 6x to 15x and is unmerged; the repo has no merge queue, so this page can land
// first. Written out as a literal it would promise 15x against a 6x slider —
// derived, it says 6x today and 15x the moment #551 lands, true in both worlds.
const MAX_SPEED_RANGE = { min: String(MAX_SPEED_MIN), max: String(MAX_SPEED_MAX) };
const MAX_SPEED_RANGE_LABEL = `${MAX_SPEED_RANGE.min}× to ${MAX_SPEED_RANGE.max}×`;

const PAGE_TITLE = 'MotionPace — The Timelapse That Paces Itself | Tweedia Studio';
const PAGE_DESCRIPTION =
  'MotionPace reads the motion in your footage and varies the playback speed to match — ' +
  'lingering on the action, flying through the still hours, with nothing cut out. ' +
  'No keyframes, no scripts, no editing.';
const CANONICAL_PATH = '/motionpace';

const HOW_IT_WORKS = [
  {
    id: 'upload',
    title: 'Upload your footage',
    text:
      'A folder of interval photos or a video file, straight off the card. Jobsite cam, GoPro, ' +
      'phone, DSLR, a Pi on a fence post — if it has timestamps, it works.',
    art: (
      <svg width="128" height="46" viewBox="0 0 128 46" fill="none" stroke="#00B3A6" strokeWidth="1.5">
        <rect x="16" y="14" width="20" height="15" rx="2" strokeOpacity=".45" />
        <rect x="40" y="14" width="20" height="15" rx="2" strokeOpacity=".65" />
        <rect x="64" y="14" width="20" height="15" rx="2" strokeOpacity=".85" />
        <path d="M96 34h16M104 34V13m0 0-5 6m5-6 5 6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 36h74" stroke="#3a424f" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'measure',
    title: 'We measure motion, frame by frame',
    text:
      'One pass scores how much actually changed between every pair of frames, then smooths it so ' +
      "a passing truck or a cloud shadow can't cause a sudden jump in speed.",
    art: (
      <svg width="128" height="46" viewBox="0 0 128 46" fill="none">
        <g stroke="#5a6572" strokeWidth="3" strokeLinecap="round">
          <path d="M14 34v-6M22 34v-13M30 34v-9M38 34v-19M46 34v-24M54 34v-16M62 34v-6M70 34v-4M78 34v-11M86 34v-21M94 34v-14M102 34v-5M110 34v-8" />
        </g>
        <path d="M14 36h100" stroke="#3a424f" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M46 8v30" stroke="#00d4c5" strokeWidth="1.5" />
        <circle cx="46" cy="10" r="3" fill="#00d4c5" />
      </svg>
    ),
  },
  {
    id: 'render',
    title: 'Get a perfectly paced timelapse',
    // NOT "reads like a camera move": the camera never moves in a fixed-position
    // timelapse, so the phrase describes something the viewer does not see. What
    // a long ramp actually buys is that the change is imperceptible.
    text:
      'Speed follows the motion, frame for frame, with ramps so gradual you never feel the ' +
      'change. Nothing is cut out — the whole shoot is still in there, just paced.',
    art: (
      <svg width="128" height="46" viewBox="0 0 128 46" fill="none">
        <g fill="none" stroke="#00B3A6" strokeWidth="1.4" strokeOpacity=".85">
          <rect x="14" y="12" width="9" height="22" rx="1.5" />
          <rect x="24" y="12" width="9" height="22" rx="1.5" />
          <rect x="34" y="12" width="9" height="22" rx="1.5" />
          <rect x="44" y="12" width="9" height="22" rx="1.5" />
          <rect x="62" y="12" width="9" height="22" rx="1.5" strokeOpacity=".5" />
          <rect x="84" y="12" width="9" height="22" rx="1.5" strokeOpacity=".5" />
          <rect x="106" y="12" width="9" height="22" rx="1.5" strokeOpacity=".5" />
        </g>
        <path d="M14 40h101" stroke="#3a424f" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const STATS = [
  {
    id: 'screen_time',
    n: '~2×',
    title: 'The busy hours get ~2× the screen time',
    text: 'Measured against a constant-speed render of the same footage at the same runtime.',
  },
  {
    id: 'ceiling',
    // The comp printed the sample render's own 6x ceiling here as though it were
    // the product's limit. The card now states the slider's real range, and the
    // sample's ceiling is named as the sample's.
    n: `${MAX_SPEED_RANGE.max}×`,
    title: `The ceiling is yours, up to ${MAX_SPEED_RANGE.max}×`,
    text:
      `The Max speed slider runs from ${MAX_SPEED_RANGE_LABEL}, and busy stretches always play ` +
      `at 1× wherever you set it. The render above was walked at a ${SAMPLE.ceiling}× ceiling and ` +
      `peaked at ${SAMPLE.speedMaxLabel}×.`,
  },
  {
    id: 'controls',
    n: '1',
    title: 'One switch. Zero keyframes.',
    text: 'Nothing to place by hand, no script to write, no clips to cut and re-time.',
  },
];

const USE_CASES = [
  {
    id: 'construction',
    title: 'Construction sites',
    text:
      'Weeks of a build in two minutes, with the pour, the framing and the crane picks still slow ' +
      'enough to follow.',
    icon: (
      <path d="M4 21V7l7-3v17M11 21h9V11l-9-4M14.5 11.5v2M14.5 16v2M17.5 11.5v2M17.5 16v2M7 10v2M7 15v2" />
    ),
  },
  {
    id: 'workshop',
    title: 'Workshop & DIY builds',
    text: 'The glue-up and the first cut get their seconds. The clamps drying overnight go by in one.',
    icon: <path d="M3 13h18M5 13v7M19 13v7M6.5 13 9 5.5h6L17.5 13M9.8 9h4.4" />,
  },
  {
    id: 'printing',
    title: '3D printing',
    text:
      'Runs through the hundred identical early layers and slows back down when the shape actually ' +
      'starts to change.',
    icon: <path d="M4 4h16M6 4v4l3 2.5M12 10.5 15 8V4M12 10.5V13M7 20h10M7 17.5h10M8 15h8" />,
  },
  {
    id: 'nature',
    title: 'Nature & sky',
    text:
      "A storm front rolling in gets room to breathe; a flat blue afternoon does not get four " +
      "minutes of your viewer's life.",
    icon: (
      <>
        <circle cx="8" cy="8" r="3" />
        <path d="M20 17H9a3 3 0 1 1 .6-5.9A4 4 0 0 1 17.5 12a2.5 2.5 0 0 1 2.5 5ZM3 21h18" />
      </>
    ),
  },
];

// THE single source for the FAQ. It renders the visible section AND feeds the
// FAQPage JSON-LD in buildJsonLd(), so the two can never disagree — a rich-text
// answer would force a second, hand-maintained plain-text copy for the structured
// data, which is exactly how those drift. Where the comp bolds a control name,
// `emphasize` names it and withEmphasis() re-applies the styling at render time
// without forking the string.
const FAQ = [
  {
    q: 'What is adaptive pacing?',
    a:
      'Playback speed that changes with what is in the frame. Tweedia measures motion across every ' +
      'pair of frames in your footage and sets a speed for each moment — slow where something is ' +
      'happening, fast where nothing is.',
    // The reciprocal half of the interlink with the pillar page that defines the
    // term (#546). A separate field rather than a link inside `a`: the answer
    // string is published verbatim as FAQPage markup, and markup cannot carry an
    // anchor — so the link renders AFTER the answer and the two never diverge.
    // Safe to hardcode: both pages are gated on the same switch, so /motionpace
    // existing at all guarantees this destination exists too.
    //
    // The label is NOT the question again. Repeating the card's own heading
    // under the answer read as a link back to the card you were already
    // reading; naming the destination says where it goes instead.
    more: { to: '/guides/what-is-adaptive-pacing', label: 'Read the guide →' },
  },
  {
    q: 'How is this different from a hyperlapse or speed ramping?',
    a:
      'A hyperlapse plays at one constant rate from start to finish, and speed ramping is you ' +
      'placing keyframes by hand. MotionPace is automatic and content-aware: the footage decides ' +
      'where the speed changes, and it can change hundreds of times in a single clip without you ' +
      'touching a timeline.',
  },
  {
    q: 'Can I control how fast it goes?',
    a:
      `Two controls. Max speed caps how far it may run ahead in the quiet stretches, from ` +
      `${MAX_SPEED_RANGE_LABEL}; Smoothness sets how long a speed change takes, from snappy to a ` +
      `gentle ramp. Busy stretches always play at 1×, and tuning is free — the preview updates ` +
      `before you render.`,
    emphasize: ['Max speed', 'Smoothness'],
  },
  {
    q: 'Does it work with photo sequences and video?',
    a:
      'Both. Drop in a folder of interval photos or a video file; photos are compiled into a ' +
      'sequence first, then paced. Its sibling MotionTrim cuts the still stretches out entirely ' +
      'instead of speeding through them — same motion pass, same two sliders, your choice per edit.',
  },
];

const escapeForRegExp = (term) => term.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');

function withEmphasis(text, terms) {
  if (!terms || terms.length === 0) return text;
  const pattern = new RegExp(`(${terms.map(escapeForRegExp).join('|')})`, 'g');
  return text.split(pattern).map((part, index) =>
    terms.includes(part) ? <b key={`${part}-${index}`}>{part}</b> : part
  );
}

function buildJsonLd() {
  const url = `https://tweediastudio.com${CANONICAL_PATH}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function MotionPacePage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonicalPath: CANONICAL_PATH,
    jsonLd: buildJsonLd(),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // The demo clip is this page's opening argument — the PR that added it dropped
  // the speed-dial section on the grounds that the clip now carries it — so a
  // clip that never starts leaves the argument unmade. Two ways that happens:
  // the visitor asked for reduced motion (WCAG 2.2.2 also wants a pause control
  // on 29s of looping motion), or the browser refused autoplay outright (iOS Low
  // Power Mode, Chrome Data Saver, enterprise policy). The poster covers the
  // still comparison either way; controls give the visitor a way back.
  const [reduceMotion] = useState(prefersReducedMotion);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const showDemoControls = reduceMotion || autoplayBlocked;

  // Everything below is set on the ELEMENT rather than left to the JSX above,
  // because /motionpace ships prerendered and JSX alone does not survive that.
  // The snapshot is taken with motion allowed (scripts/prerender.js opens the
  // page with no `reducedMotion` override) and written out as raw outerHTML, so
  // the file always carries `autoplay loop` and never `controls` — and React 18
  // hydration warns about mismatched attributes without patching them, while
  // `reduceMotion` is initial state that never re-renders. `muted` used to be
  // worse still — React sets it as a DOM property only, so it reached no
  // snapshot at all and the served HTML was an UNMUTED autoplay that Chrome
  // refuses on a first visit; `forceMuted` below now writes the attribute, so
  // the snapshot carries it and playback no longer depends on this asset
  // happening to have no audio track (#582).
  const demoVideoRef = useCallback(
    (el) => {
      if (!el) return;
      // Writes the ATTRIBUTE as well as the property React skips. The property
      // alone keeps autoplay legal on the hydrated node if this clip is ever
      // re-encoded with sound, but it reaches no snapshot — so the prerendered
      // HTML served to a cold visitor would still say `<video autoplay>` with
      // no `muted`, which Chrome refuses to start (#582). See lib/forceMuted.
      forceMuted(el);
      if (reduceMotion) {
        el.autoplay = false;
        el.loop = false;
        el.controls = true;
        el.pause?.(); // the prerendered HTML may already have started it
        return;
      }
      // autoPlay already asked; this surfaces the refusal so we can offer controls.
      el.play?.()?.catch?.(() => setAutoplayBlocked(true));
    },
    [reduceMotion]
  );

  const handleCtaClick = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: signedIn });
  };

  const handleSignInCta = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: false });
    signIn();
  };

  // Internal cross-links report `nav_click`, not `cta_click` — the same split
  // the guides use, so the CTA funnel is not polluted by navigation.
  const handleNavClick = (target) => () => {
    trackEvent('nav_click', { target });
  };

  // Same shape as the landing page's hero CTA: signed in, go to the studio;
  // signed out, open the sign-in flow rather than a dead /studio redirect.
  const renderPrimaryCta = (ctaId, label, testId) =>
    signedIn ? (
      <Link
        to="/studio"
        className="mp-cta mp-cta-primary"
        data-testid={testId}
        onClick={handleCtaClick(ctaId)}
      >
        {label}
      </Link>
    ) : (
      <button
        type="button"
        className="mp-cta mp-cta-primary"
        data-testid={testId}
        onClick={handleSignInCta(ctaId)}
      >
        {label}
      </button>
    );

  return (
    <div className="motionpace-page">
      <Header user={user} signIn={signIn} />
      <main className="mp-main" data-testid="motionpace-main">
        {/* ---------------- 1 · hero ---------------- */}
        <section className="mp-hero" id="mp-hero" data-testid="motionpace-hero">
          <div className="mp-hero-glow" aria-hidden="true" />
          <div className="mp-wrap">
            <span className="mp-badge">
              <i className="mp-badge-dot" aria-hidden="true" />
              New in Tweedia Studio
            </span>
            <h1>The timelapse that&nbsp;paces itself.</h1>
            <p className="mp-hero-sub">
              MotionPace™ reads the motion in your footage and adapts the speed automatically —
              lingering on the action, flying through the still hours.
              <b>No keyframes. No scripts. No editing.</b>
            </p>
            <div className="mp-hero-ctas">
              {renderPrimaryCta('motionpace_hero', 'Try MotionPace free', 'motionpace-cta-hero')}
              <a
                className="mp-cta mp-cta-secondary"
                href="#mp-compare"
                data-testid="motionpace-cta-hero-secondary"
                onClick={handleCtaClick('motionpace_hero_compare')}
              >
                See it side by side
              </a>
            </div>
            <p className="mp-hero-note">
              Free plan includes starter credits · no card, no camera subscription
            </p>

            {/* ---- the demo: the claim, moving, before the chart explains it ----
                Two real renders of the same day played side by side. Muted +
                autoplay + loop + playsInline is the combination browsers allow
                to start without a gesture.

                autoPlay is kept (rather than starting on intersection, the way
                the homepage's below-fold pair does) because this sits above the
                fold on a PRERENDERED route: the crawler's HTML and a no-JS
                visitor's HTML are the same file, and without autoPlay in it they
                get a dead poster with no play button. The cost is that
                `preload` is inert while autoPlay is on — the browser fetches
                media data regardless — so the payload is held down by the asset
                itself (720p, 2.8 MB) rather than by a preload hint.

                jsdom has no video engine, so the tests assert the attributes
                rather than playback. */}
            <figure className="mp-panel mp-panel-demo" data-testid="motionpace-demo">
              <video
                ref={demoVideoRef}
                autoPlay={!reduceMotion}
                muted
                loop={!reduceMotion}
                playsInline
                controls={showDemoControls}
                preload="metadata"
                poster={ASSETS.demoPoster}
                data-testid="motionpace-demo-video"
                aria-label="Side-by-side comparison: constant speed versus MotionPace on the same deck-build footage"
              >
                <source
                  src={ASSETS.demoVideo}
                  type="video/mp4"
                  data-testid="motionpace-demo-source"
                />
              </video>
              <figcaption className="mp-demo-cap">
                <b>Left: constant speed. Right: MotionPace on the same footage, same runtime.</b>{' '}
                The dial in the corner is the optional pace overlay, spinning with the playback
                speed.
              </figcaption>
            </figure>

            {/* ---- the one image that explains the feature ---- */}
            {/* Chart + strip extracted into MotionChartHero so the landing
                section can embed the same visual without duplicating the SVG. */}
            <figure className="mp-panel" data-testid="motionpace-chart">
              <MotionChartHero showFooter />
            </figure>
          </div>
        </section>

        {/* ---------------- 2 · how it works ---------------- */}
        <section className="mp-section" id="mp-how" data-testid="motionpace-how">
          <div className="mp-wrap">
            <p className="mp-kicker">How it works</p>
            <h2 className="mp-section-title">Three steps, none of them editing</h2>
            <p className="mp-lede">
              MotionPace runs on the same motion pass Tweedia already does when it ingests your
              footage — so pacing costs you nothing but a switch.
            </p>

            <ol className="mp-steps" data-testid="motionpace-steps">
              {HOW_IT_WORKS.map((step, index) => (
                <li className="mp-step" key={step.id}>
                  <div className="mp-step-art" aria-hidden="true">
                    {step.art}
                  </div>
                  <div className="mp-step-head">
                    <span className="mp-step-num" aria-hidden="true">
                      {index + 1}
                    </span>
                    <h3>{step.title}</h3>
                  </div>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ---------------- 3 · constant speed vs MotionPace ---------------- */}
        <section
          className="mp-section mp-compare-section"
          id="mp-compare"
          data-testid="motionpace-compare"
        >
          <div className="mp-wrap">
            <p className="mp-kicker">Constant speed vs adaptive pacing</p>
            <h2 className="mp-section-title">Same frames. Same runtime. Better minutes.</h2>
            <p className="mp-lede">
              A constant-speed render spends as long on an empty yard as it does on the day the deck
              goes up. Adaptive pacing spends the runtime where the work is.
            </p>

            <div className="mp-cmp">
              <div className="mp-cmp-row">
                <div className="mp-cmp-label">
                  <span className="mp-tag mp-tag-grey">Constant {SAMPLE.speedMeanLabel}×</span>
                  <b>Every frame gets the same slice of the clock</b>
                  {/* Why 2.2x and not 1x, which is the first thing anyone asks:
                      a 1x constant render IS the full source runtime, so the
                      only constant speed that lands on the same output length
                      as the paced render is the paced render's own mean. Both
                      numbers are derived from SAMPLE, so the sentence cannot
                      drift from the tag beside it. */}
                  <span className="mp-cmp-say">
                    — the one constant speed that fits {SAMPLE.sourceLabel} of footage into{' '}
                    {SAMPLE.outLabel}
                  </span>
                </div>
                <FilmStrip variant="const" cells={CONST_CELLS} sprite={ASSETS.stripConstant} />
              </div>
              <div className="mp-cmp-row">
                <div className="mp-cmp-label">
                  <span className="mp-tag mp-tag-teal">MotionPace</span>
                  <b>Frames bunch where the work is</b>
                  <span className="mp-cmp-say">
                    — 1× through the build, up to {SAMPLE.speedMaxLabel}× through the quiet
                  </span>
                </div>
                <FilmStrip variant="pace" cells={HERO_CELLS} sprite={ASSETS.stripPace} />
                <TimeRuler className="mp-cmp-ruler" />
              </div>
              <p className="mp-cmp-foot">
                <b>
                  Both rows are {SAMPLE.outFramesLabel}-frame, {SAMPLE.outLabel} renders of the same{' '}
                  {SAMPLE.sourceLabel} shoot
                </b>{' '}
                — laid out over the moment each frame was captured. Only the distribution changes.
              </p>
            </div>

            <div className="mp-stats" data-testid="motionpace-stats">
              {STATS.map((stat) => (
                <div className="mp-stat" key={stat.id}>
                  <div className="mp-stat-n">{stat.n}</div>
                  <h3>{stat.title}</h3>
                  <p>{stat.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The speed-dial section that used to sit here is gone. Three stills of
            a corner crop spent a full screen proving a 72px overlay exists,
            which the demo video at the top now does in motion and in passing —
            a spinning dial is self-explanatory, a still one is a smudge. The
            video's caption carries the one line that section was for. */}

        {/* ---------------- 4 · use cases ---------------- */}
        <section
          className="mp-section mp-uses-section"
          id="mp-uses"
          data-testid="motionpace-uses"
        >
          <div className="mp-wrap">
            <p className="mp-kicker">Where it earns its keep</p>
            <h2 className="mp-section-title">Anything slow, filmed from one spot</h2>
            <p className="mp-lede">
              Adaptive pacing pays off wherever the interesting part is a small fraction of the shoot
              — which is nearly every timelapse worth making.
            </p>

            <div className="mp-uses">
              {USE_CASES.map((useCase) => (
                <div className="mp-use" key={useCase.id}>
                  <div className="mp-use-ico" aria-hidden="true">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#00B3A6"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {useCase.icon}
                    </svg>
                  </div>
                  <h3>{useCase.title}</h3>
                  <p>{useCase.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- 5 · FAQ ---------------- */}
        <section className="mp-section mp-faq-section" id="mp-faq" data-testid="motionpace-faq">
          <div className="mp-wrap">
            <h2 className="mp-section-title">Questions people actually ask</h2>
            <div className="mp-faq">
              {FAQ.map((item) => (
                <div className="mp-faq-item" key={item.q}>
                  <h3>{item.q}</h3>
                  <p>{withEmphasis(item.a, item.emphasize)}</p>
                  {item.more && (
                    <p className="mp-faq-more">
                      <Link
                        to={item.more.to}
                        data-testid="motionpace-adaptive-pacing-link"
                        onClick={handleNavClick('adaptive_pacing_from_motionpace')}
                      >
                        {item.more.label}
                      </Link>
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- 6 · final CTA ---------------- */}
        <div className="mp-wrap">
          <section className="mp-final" id="mp-start" data-testid="motionpace-final-cta">
            <h2>Let the footage set the pace.</h2>
            <p>
              Upload a shoot, flip one switch, and watch a paced draft before you spend a credit.
            </p>
            <div className="mp-final-row">
              {renderPrimaryCta('motionpace_final', 'Try MotionPace free', 'motionpace-cta-final')}
              <Link
                to="/pricing"
                className="mp-cta mp-cta-secondary"
                data-testid="motionpace-cta-final-secondary"
                onClick={handleCtaClick('pricing_from_motionpace')}
              >
                See the plans
              </Link>
            </div>
            <p className="mp-fine">
              Tuning and previewing are always free. Credits are charged on a finished render, and a
              failed render refunds automatically.
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default MotionPacePage;
export { FAQ, STATS, HOW_IT_WORKS, USE_CASES, MAX_SPEED_RANGE, buildJsonLd };
